/**
 * Conversation Notes Service - Phase 6A: Internal Agent Communication
 * 
 * Provides API service for managing conversation notes with comprehensive
 * error handling, logging, and type safety following existing service patterns.
 */

import { api } from './api'
import { logger } from '../utils/logger'
import type {
  ConversationNote,
  CreateConversationNoteRequest,
  UpdateConversationNoteRequest,
  ConversationNotesListResponse
} from '../types'

export interface GetNotesOptions {
  page?: number
  page_size?: number
  note_type?: string
  priority?: string
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

export interface ConversationNotesService {
  getNotes(conversationId: string, options?: GetNotesOptions): Promise<ConversationNotesListResponse>
  createNote(noteData: CreateConversationNoteRequest): Promise<ConversationNote>
  updateNote(noteId: string, updateData: UpdateConversationNoteRequest): Promise<ConversationNote>
  deleteNote(noteId: string): Promise<void>
}

class ConversationNotesServiceImpl implements ConversationNotesService {
  /**
   * Get all notes for a conversation with optional filtering and pagination
   */
  async getNotes(
    conversationId: string, 
    options: GetNotesOptions = {}
  ): Promise<ConversationNotesListResponse> {
    logger.info('Fetching conversation notes', {
      conversationId,
      options,
      action: 'get_notes'
    })

    try {
      const params = new URLSearchParams()
      
      // Add pagination parameters
      if (options.page) params.append('page', options.page.toString())
      if (options.page_size) params.append('page_size', options.page_size.toString())
      
      // Add filtering parameters
      if (options.note_type) params.append('note_type', options.note_type)
      if (options.priority) params.append('priority', options.priority)
      if (options.sort_by) params.append('sort_by', options.sort_by)
      if (options.sort_order) params.append('sort_order', options.sort_order)

      const queryString = params.toString()
      const url = `/conversations/${conversationId}/notes${queryString ? `?${queryString}` : ''}`
      
      const response = await api.get<ConversationNotesListResponse>(url)
      
      logger.info('Successfully fetched conversation notes', {
        conversationId,
        notesCount: response.data.notes.length,
        totalCount: response.data.total_count,
        page: response.data.page,
        hasMore: response.data.has_more,
        action: 'get_notes_success'
      })

      return response.data
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch conversation notes'
      
      logger.error('Failed to fetch conversation notes', {
        conversationId,
        error: errorMessage,
        options,
        action: 'get_notes_error'
      })

      // Transform common API errors into user-friendly messages
      if (error instanceof Error && error.message.includes('403')) {
        throw new Error('You don\'t have permission to view notes for this conversation')
      } else if (error instanceof Error && error.message.includes('404')) {
        throw new Error('Conversation not found')
      } else {
        throw new Error(errorMessage)
      }
    }
  }

  /**
   * Create a new conversation note
   */
  async createNote(noteData: CreateConversationNoteRequest): Promise<ConversationNote> {
    logger.info('Creating new conversation note', {
      conversationId: noteData.conversation_id,
      noteType: noteData.note_type,
      priority: noteData.priority,
      visibility: noteData.visibility,
      isPinned: noteData.is_pinned,
      contentLength: noteData.content.length,
      action: 'create_note'
    })

    try {
      // Validate required fields
      if (!noteData.conversation_id?.trim()) {
        throw new Error('Conversation ID is required')
      }
      if (!noteData.content?.trim()) {
        throw new Error('Note content is required')
      }
      if (noteData.content.length > 5000) {
        throw new Error('Note content must be 5000 characters or less')
      }

      const response = await api.post<ConversationNote>(
        `/conversations/${noteData.conversation_id}/notes`,
        noteData
      )

      logger.info('Successfully created conversation note', {
        noteId: response.data.id,
        conversationId: noteData.conversation_id,
        noteType: response.data.note_type,
        priority: response.data.priority,
        action: 'create_note_success'
      })

      return response.data
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create conversation note'
      
      logger.error('Failed to create conversation note', {
        conversationId: noteData.conversation_id,
        error: errorMessage,
        noteData: {
          ...noteData,
          content: noteData.content.substring(0, 100) + '...' // Truncate for logging
        },
        action: 'create_note_error'
      })

      // Transform API errors into user-friendly messages
      if (error instanceof Error && error.message.includes('403')) {
        throw new Error('You don\'t have permission to add notes to this conversation')
      } else if (error instanceof Error && error.message.includes('400')) {
        throw new Error('Invalid note data. Please check your input and try again.')
      } else {
        throw new Error(errorMessage)
      }
    }
  }

  /**
   * Update an existing conversation note
   */
  async updateNote(noteId: string, updateData: UpdateConversationNoteRequest): Promise<ConversationNote> {
    logger.info('Updating conversation note', {
      noteId,
      updateFields: Object.keys(updateData),
      action: 'update_note'
    })

    try {
      // Validate input
      if (!noteId?.trim()) {
        throw new Error('Note ID is required')
      }
      if (updateData.content !== undefined && !updateData.content?.trim()) {
        throw new Error('Note content cannot be empty')
      }
      if (updateData.content && updateData.content.length > 5000) {
        throw new Error('Note content must be 5000 characters or less')
      }

      const response = await api.put<ConversationNote>(
        `/conversations/notes/${noteId}`,
        updateData
      )

      logger.info('Successfully updated conversation note', {
        noteId,
        updatedFields: Object.keys(updateData),
        isEdited: response.data.is_edited,
        action: 'update_note_success'
      })

      return response.data
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update conversation note'
      
      logger.error('Failed to update conversation note', {
        noteId,
        error: errorMessage,
        updateData,
        action: 'update_note_error'
      })

      // Transform API errors into user-friendly messages
      if (error instanceof Error && error.message.includes('403')) {
        throw new Error('You don\'t have permission to edit this note')
      } else if (error instanceof Error && error.message.includes('404')) {
        throw new Error('Note not found')
      } else if (error instanceof Error && error.message.includes('400')) {
        throw new Error('Invalid note data. Please check your input and try again.')
      } else {
        throw new Error(errorMessage)
      }
    }
  }

  /**
   * Delete a conversation note
   */
  async deleteNote(noteId: string): Promise<void> {
    logger.info('Deleting conversation note', {
      noteId,
      action: 'delete_note'
    })

    try {
      // Validate input
      if (!noteId?.trim()) {
        throw new Error('Note ID is required')
      }

      await api.delete(`/conversations/notes/${noteId}`)

      logger.info('Successfully deleted conversation note', {
        noteId,
        action: 'delete_note_success'
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete conversation note'
      
      logger.error('Failed to delete conversation note', {
        noteId,
        error: errorMessage,
        action: 'delete_note_error'
      })

      // Transform API errors into user-friendly messages
      if (error instanceof Error && error.message.includes('403')) {
        throw new Error('You don\'t have permission to delete this note')
      } else if (error instanceof Error && error.message.includes('404')) {
        throw new Error('Note not found')
      } else {
        throw new Error(errorMessage)
      }
    }
  }
}

// Export singleton instance following existing service pattern
export const conversationNotesService = new ConversationNotesServiceImpl()

// Export for dependency injection if needed
export default conversationNotesService